import i18next from 'i18next'
import { DeliveryModel } from '@audibene/ta.utils-opportunity'
import { AppointmentRescheduleReason, Country, networkDeliveryModels } from '../../../../../constants'

type PrimaryAppointmentRescheduleReason =
  | 'CUSTOMER_NOT_AVAILABLE'
  | 'OTHER'
  | 'TECH_SETUP_FAILED'
  | 'WAX_OCCLUSION'
  | 'PROVIDER_ILLNESS'
  | 'PROVIDER_UNAVAILABLE'

const PrimaryAppointmentRescheduleReason: Record<PrimaryAppointmentRescheduleReason, PrimaryAppointmentRescheduleReason> = {
  CUSTOMER_NOT_AVAILABLE: 'CUSTOMER_NOT_AVAILABLE',
  OTHER: 'OTHER',
  TECH_SETUP_FAILED: 'TECH_SETUP_FAILED',
  WAX_OCCLUSION: 'WAX_OCCLUSION',
  PROVIDER_ILLNESS: 'PROVIDER_ILLNESS',
  PROVIDER_UNAVAILABLE: 'PROVIDER_UNAVAILABLE',
} as const

type PrimaryAppointmentRescheduleReasonDE = 'CUSTOMER' | 'PARTNER' | 'TECH' | 'TECH_SETUP_INCOMPLETE'

const PrimaryAppointmentRescheduleReasonDE: Record<PrimaryAppointmentRescheduleReasonDE, PrimaryAppointmentRescheduleReasonDE> = {
  CUSTOMER: 'CUSTOMER',
  PARTNER: 'PARTNER',
  TECH: 'TECH',
  TECH_SETUP_INCOMPLETE: 'TECH_SETUP_INCOMPLETE',
} as const

const primaryReasonDisplayText: Record<PrimaryAppointmentRescheduleReason, string> = {
  CUSTOMER_NOT_AVAILABLE: i18next.t('pages.appointment.reschedule.reason.customerNotAvailable'),
  OTHER: i18next.t('pages.appointment.reschedule.reason.other'),
  TECH_SETUP_FAILED: i18next.t('pages.appointment.reschedule.reason.techSetupFailed'),
  WAX_OCCLUSION: i18next.t('pages.appointment.reschedule.reason.waxOcclusion'),
  PROVIDER_ILLNESS: i18next.t('pages.appointment.reschedule.reason.providerIllness'),
  PROVIDER_UNAVAILABLE: i18next.t('pages.appointment.reschedule.reason.providerUnavailable'),
} as const

const primaryReasonDisplayTextDE: Record<PrimaryAppointmentRescheduleReasonDE, string> = {
  CUSTOMER: i18next.t('pages.appointment.reason.customer'),
  PARTNER: i18next.t('pages.appointment.reason.partner'),
  TECH: i18next.t('pages.appointment.reason.tech'),
  TECH_SETUP_INCOMPLETE: i18next.t('pages.appointment.reason.techSetupIncomplete'),
} as const

const networkRescheduleReasons = [
  AppointmentRescheduleReason.SCHEDULE_CONFLICT,
  AppointmentRescheduleReason.WANTS_DIFFERENT_TIME,
  AppointmentRescheduleReason.NOT_READY,
  AppointmentRescheduleReason.TRAVEL_TRANSPORTATION_ISSUE,
  AppointmentRescheduleReason.HEALTH_ISSUE,
  AppointmentRescheduleReason.CLINIC_PROVIDER_RESCHEDULE,
  AppointmentRescheduleReason.OTHER,
] as const

const primaryReasonDisplayTextNetwork: Record<(typeof networkRescheduleReasons)[number], string> = {
  [AppointmentRescheduleReason.SCHEDULE_CONFLICT]: i18next.t('pages.appointment.reschedule.reason.network.scheduleConflict'),
  [AppointmentRescheduleReason.WANTS_DIFFERENT_TIME]: i18next.t('pages.appointment.reschedule.reason.network.wantsDifferentTime'),
  [AppointmentRescheduleReason.NOT_READY]: i18next.t('pages.appointment.reschedule.reason.network.notReady'),
  [AppointmentRescheduleReason.TRAVEL_TRANSPORTATION_ISSUE]: i18next.t(
    'pages.appointment.reschedule.reason.network.travelTransportationIssue'
  ),
  [AppointmentRescheduleReason.HEALTH_ISSUE]: i18next.t('pages.appointment.reschedule.reason.network.healthIssue'),
  [AppointmentRescheduleReason.CLINIC_PROVIDER_RESCHEDULE]: i18next.t(
    'pages.appointment.reschedule.reason.network.clinicProviderReschedule'
  ),
  [AppointmentRescheduleReason.OTHER]: i18next.t('pages.appointment.reschedule.reason.other'),
} as const

const pooledSchedulingOnlyReasons = new Set<string>([
  PrimaryAppointmentRescheduleReason.PROVIDER_ILLNESS,
  PrimaryAppointmentRescheduleReason.PROVIDER_UNAVAILABLE,
])
const primaryRescheduleReasons = Object.values(PrimaryAppointmentRescheduleReason)
const primaryRescheduleReasonsWithoutPooledScheduling = primaryRescheduleReasons.filter(
  (reason) => !pooledSchedulingOnlyReasons.has(reason)
)

function getPrimaryRescheduleReasons(
  country: Country,
  deliveryModel: DeliveryModel | undefined,
  isPooledSchedulingOn: boolean | undefined
): readonly string[] {
  if (country === Country.DE) return Object.values(PrimaryAppointmentRescheduleReasonDE)
  if (deliveryModel && networkDeliveryModels.has(deliveryModel)) return networkRescheduleReasons
  return isPooledSchedulingOn === true ? primaryRescheduleReasons : primaryRescheduleReasonsWithoutPooledScheduling
}

function getPrimaryRescheduleReasonDisplayText(country: Country, deliveryModel?: DeliveryModel): Record<string, string> {
  if (country === Country.DE) return primaryReasonDisplayTextDE
  if (deliveryModel && networkDeliveryModels.has(deliveryModel)) return primaryReasonDisplayTextNetwork
  return primaryReasonDisplayText
}

export {
  getPrimaryRescheduleReasonDisplayText,
  getPrimaryRescheduleReasons,
  PrimaryAppointmentRescheduleReason,
  PrimaryAppointmentRescheduleReasonDE,
  primaryReasonDisplayText as primaryAppointmentRescheduleReasonDisplayText,
}
